import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  FormControl,
  OutlinedInput,
  Typography,
  Button,
  Select,
  MenuItem,
  Switch,
  FormHelperText,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import { Upload } from "react-iconly";
import { Formik } from "formik";
import * as yup from "yup";
import "./index.scss";
import { Images } from "./../../assets/common";
import ApiService from "../../services/api-service";
import {
  getErrorString,
  validateForm,
  isEmpty,
} from "../../constants/function";
import notifier from "../../components/Notifier";
import SuccessPopup from "../../components/SuccessPopup";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";

const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  studioId: yup.string().required("Please select a studio/playlist"),
  videoFile: yup.mixed().required("Video file is required"),
  explicitContent: yup.boolean(),
});

export default function UploadVideo() {
  const { t } = useTranslation("common");
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [studios, setStudios] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      // Pass empty object and true to use MY_PLAYLIST endpoint
      const response = await ApiService.PLAYLIST({}, true);
      setStudios(response.result || []);
    } catch (error) {
      console.error("Error fetching studios:", error);
      notifier.error("Failed to load studios/playlists");
    }
  };

  const handleVideoChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      const validTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-flv', 'video/webm'];
      if (!validTypes.includes(file.type)) {
        notifier.error("Please select a valid video file (MP4, MPEG, MOV, AVI, FLV, WebM)");
        return;
      }

      // Check file size (500MB limit)
      const maxSize = 500 * 1024 * 1024;
      if (file.size > maxSize) {
        notifier.error("Video file size must be less than 500MB");
        return;
      }

      setSelectedVideo(file);
      setFieldValue("videoFile", file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append("file", values.videoFile, values.videoFile.name);
      formData.append("title", values.title);
      formData.append("studioId", values.studioId);
      formData.append("isExplicit", values.explicitContent ? 'true' : 'false');

      const response = await ApiService.UPVID(values.studioId, formData);
      
      if (response.code === 200) {
        setSuccess(true);
        setTimeout(() => {
          history.push('/app/Library');
        }, 2000);
      } else {
        notifier.error(response.message || "Failed to upload video");
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    history.push('/app/Library');
  };

  return (
    <Box className="upload-video-page">
      <PageHeader title={t("Upload Video")} />
      
      <Box className="upload-video-container" sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
        <Formik
          initialValues={{
            title: "",
            studioId: "",
            videoFile: null,
            explicitContent: false,
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* Studio Selection */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Select Studio/Playlist *
                  </Typography>
                  <FormControl fullWidth error={touched.studioId && !!errors.studioId}>
                    <Select
                      name="studioId"
                      value={values.studioId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      displayEmpty
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a studio/playlist</em>
                      </MenuItem>
                      {studios.length === 0 ? (
                        <MenuItem disabled>
                          <em>No studios found. Please create one first.</em>
                        </MenuItem>
                      ) : (
                        studios.map((studio) => (
                          <MenuItem key={studio._id} value={studio._id}>
                            {studio.title}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.studioId && errors.studioId && (
                      <FormHelperText>{errors.studioId}</FormHelperText>
                    )}
                  </FormControl>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Don't have a studio?
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => history.push('/app/Playlists/create')}
                      sx={{
                        color: "#ffee20",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: "rgba(255, 238, 32, 0.1)",
                        },
                      }}
                    >
                      Create one
                    </Button>
                  </Box>
                </Box>

                {/* Video Title */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Video Title *
                  </Typography>
                  <OutlinedInput
                    name="title"
                    placeholder="Enter video title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.title && !!errors.title}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: touched.title && errors.title ? "error.main" : "rgba(255, 255, 255, 0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: touched.title && errors.title ? "error.main" : "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                  />
                  {touched.title && errors.title && (
                    <FormHelperText error>{errors.title}</FormHelperText>
                  )}
                </Box>

                {/* Video Upload */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Upload Video *
                  </Typography>
                  <Box
                    sx={{
                      border: "2px dashed rgba(255, 255, 255, 0.3)",
                      borderRadius: 2,
                      p: 4,
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      "&:hover": {
                        borderColor: "#ffee20",
                        backgroundColor: "rgba(255, 238, 32, 0.05)",
                      },
                    }}
                    onClick={() => document.getElementById("video-upload").click()}
                  >
                    <input
                      id="video-upload"
                      type="file"
                      accept="video/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleVideoChange(e, setFieldValue)}
                    />
                    
                    {videoPreview ? (
                      <Box>
                        <video
                          src={videoPreview}
                          controls
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "8px",
                          }}
                        />
                        <Typography sx={{ mt: 2 }}>
                          {selectedVideo?.name}
                        </Typography>
                        <Button
                          variant="text"
                          sx={{ mt: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVideo(null);
                            setVideoPreview(null);
                            setFieldValue("videoFile", null);
                          }}
                        >
                          Remove Video
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Upload size={48} style={{ marginBottom: 16, color: "#ffee20" }} />
                        <Typography variant="h6">
                          Click to upload video
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                          MP4, MPEG, MOV, AVI, FLV, WebM (Max 500MB)
                        </Typography>
                      </>
                    )}
                  </Box>
                  {touched.videoFile && errors.videoFile && (
                    <FormHelperText error>{errors.videoFile}</FormHelperText>
                  )}
                </Box>

                {/* Explicit Content */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Explicit Content
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Does this video contain explicit content?
                    </Typography>
                  </Box>
                  <Switch
                    name="explicitContent"
                    checked={values.explicitContent}
                    onChange={(e) => setFieldValue("explicitContent", e.target.checked)}
                  />
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !values.videoFile || !values.title || !values.studioId}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    backgroundColor: "#ffee20",
                    color: "#000",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    "&:hover": {
                      backgroundColor: "#e6d61c",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(255, 238, 32, 0.3)",
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  }}
                >
                  {loading ? "Uploading..." : "Upload Video"}
                </Button>
              </Stack>
            </form>
          )}
        </Formik>
      </Box>

      {loading && <Loader message="Uploading your video..." />}
      
      <SuccessPopup
        open={success}
        handleClose={handleClose}
        message={t("Video uploaded successfully!")}
      />
    </Box>
  );
}